import { IPredictor, ToolDecoder } from './interfaces';
import { clamp } from './mathUtils';
import { invalidEnum } from './baseUtils';

export function encodePressure(p: number) {
  return Math.round(p * 1023) | 0;
}

export function decodePressure(p: number) {
  return p / 1023;
}

export function roundCoord(value: number) {
  return Math.round(value * 10) * 0.1;
}

export function encodeCoord(value: number) {
  return Math.round(value * 10) | 0;
}

export function decodeCoord(value: number) {
  return value * 0.1;
}

function createPredictor(): IPredictor {
  return { a: 0, b: 0 };
}

function predictorInit(predictor: IPredictor, value: number) {
  predictor.a = value;
  predictor.b = value;
}

function predictorPush(predictor: IPredictor, value: number) {
  predictor.a = predictor.b;
  predictor.b = value;
}

function predictorNext(predictor: IPredictor) {
  return predictor.b + (predictor.b - predictor.a);
}

export const enum BufferedEntry {
  Zero = 0,
  U8 = 1,
  U16 = 2,
  U24 = 3,
  U32 = 4,
  U40 = 5,
  U64 = 6,
  Floats = 7,
}

export interface BufferedEncoder {
  x: IPredictor;
  y: IPredictor;
  p: IPredictor;
  offset: number;
  lastTypeOffset: number;
  buffer: Uint8Array;
  floats: boolean;
}

export function createBufferedEncoder(): BufferedEncoder {
  const x = createPredictor();
  const y = createPredictor();
  const p = createPredictor();
  return { x, y, p, offset: 0, lastTypeOffset: 0, buffer: new Uint8Array(16), floats: false };
}

export function startBufferedEncoder(encoder: BufferedEncoder, x: number, y: number, pressure: number) {
  if (DEVELOPMENT && encoder.offset) {
    console.error(`Resetting uncommited encoder`);
  }

  encoder.floats = false;
  encoder.offset = 0;

  if ((x | 0) !== x || (y | 0) !== y) {
    x = encodeCoord(x);
    y = encodeCoord(y);
    encoder.floats = true;
  }

  predictorInit(encoder.x, x);
  predictorInit(encoder.y, y);
  predictorInit(encoder.p, pressure);
}

function writeEntryType(encoder: BufferedEncoder, type: BufferedEntry) {
  const last = encoder.buffer[encoder.lastTypeOffset];

  if (encoder.offset && ((last & 0b111) === type) && ((last & 0b11111000) !== 0b11111000)) {
    encoder.buffer[encoder.lastTypeOffset] = (last & 0b111) | (((last >> 3) + 1) << 3);
  } else {
    encoder.lastTypeOffset = encoder.offset;
    encoder.buffer[encoder.offset] = type;
    encoder.offset += 1;
  }
}

function promoteToFloats(decoder: ToolDecoder | BufferedEncoder) {
  decoder.x.a = Math.round(decoder.x.a * 10);
  decoder.x.b = Math.round(decoder.x.b * 10);
  decoder.y.a = Math.round(decoder.y.a * 10);
  decoder.y.b = Math.round(decoder.y.b * 10);
}

export function moveBufferedEncoder(encoder: BufferedEncoder, x: number, y: number, p: number) {
  // switch to floats
  if (!encoder.floats && ((x | 0) !== x || (y | 0) !== y)) {
    promoteToFloats(encoder);
    encoder.floats = true;

    if (encoder.buffer.length === encoder.offset) doubleCapacity(encoder);
    writeEntryType(encoder, BufferedEntry.Floats);
  }

  if (encoder.floats) {
    x = encodeCoord(x);
    y = encodeCoord(y);
  }

  let dx = x - predictorNext(encoder.x);
  let dy = y - predictorNext(encoder.y);
  let dp = p - predictorNext(encoder.p);
  const left = encoder.buffer.length - encoder.offset;

  predictorPush(encoder.x, x);
  predictorPush(encoder.y, y);
  predictorPush(encoder.p, p);

  if (left < 9) doubleCapacity(encoder);

  if (dx === 0 && dy === 0 && dp === 0) {
    writeEntryType(encoder, BufferedEntry.Zero);
  } else if (dx >= -2 && dx <= 1 && dy >= -2 && dy <= 1 && dp >= -8 && dp <= 7) {
    // 2, 2, 4 (8 bits)
    if (dx < 0) dx = dx + 4;
    if (dy < 0) dy = dy + 4;
    if (dp < 0) dp = dp + 16;
    writeEntryType(encoder, BufferedEntry.U8);
    encoder.buffer[encoder.offset] = (dx << 6) | (dy << 4) | dp;
    encoder.offset += 1;
  } else if (dx >= -8 && dx <= 7 && dy >= -8 && dy <= 7 && dp >= -128 && dp <= 127) {
    // 4, 4, 8 (16 bits)
    if (dx < 0) dx = dx + 16;
    if (dy < 0) dy = dy + 16;
    if (dp < 0) dp = dp + 256;
    writeEntryType(encoder, BufferedEntry.U16);
    encoder.buffer[encoder.offset] = dp;
    encoder.buffer[encoder.offset + 1] = (dx << 4) | dy;
    encoder.offset += 2;
  } else if (dx >= -64 && dx <= 63 && dy >= -64 && dy <= 63) {
    // 7, 7, 10 (24 bits)
    if (dx < 0) dx = dx + 128;
    if (dy < 0) dy = dy + 128;
    const a = (dx << 9) | (dy << 2) | (p >> 8);
    writeEntryType(encoder, BufferedEntry.U24);
    encoder.buffer[encoder.offset] = a & 0xff;
    encoder.buffer[encoder.offset + 1] = (a >> 8) & 0xff;
    encoder.buffer[encoder.offset + 2] = p & 0xff;
    encoder.offset += 3;
  } else if (dx >= -512 && dx <= 511 && dy >= -512 && dy <= 511) {
    // 11, 11, 10 (32 bits)
    if (dx < 0) dx = dx + 1024;
    if (dy < 0) dy = dy + 1024;
    const a = ((dx << 21) | (dy << 10) | p) >>> 0;
    writeEntryType(encoder, BufferedEntry.U32);
    encoder.buffer[encoder.offset] = a & 0xff;
    encoder.buffer[encoder.offset + 1] = (a >>> 8) & 0xff;
    encoder.buffer[encoder.offset + 2] = (a >>> 16) & 0xff;
    encoder.buffer[encoder.offset + 3] = (a >>> 24) & 0xff;
    encoder.offset += 4;
  } else if (x >= -16383 && x <= 16383 && y >= -16383 && y <= 16383) {
    // 15, 15, 10 (40 bits)
    if (x < 0) x = x + 32767;
    if (y < 0) y = y + 32767;
    const a = ((x << 17) | (y << 2) | (p >> 8)) >>> 0;
    writeEntryType(encoder, BufferedEntry.U40);
    encoder.buffer[encoder.offset] = a & 0xff;
    encoder.buffer[encoder.offset + 1] = (a >>> 8) & 0xff;
    encoder.buffer[encoder.offset + 2] = (a >>> 16) & 0xff;
    encoder.buffer[encoder.offset + 3] = (a >>> 24) & 0xff;
    encoder.buffer[encoder.offset + 4] = p & 0xff;
    encoder.offset += 5;
  } else {
    // 27, 27, 10 (64 bits)
    x = clamp(x, -67108864, 67108864);
    y = clamp(y, -67108864, 67108864);
    if (x < 0) x = x + 134217728;
    if (y < 0) y = y + 134217728;
    writeEntryType(encoder, BufferedEntry.U64);
    encoder.buffer[encoder.offset + 0] = (x >> 19) & 0xff;
    encoder.buffer[encoder.offset + 1] = (x >> 11) & 0xff;
    encoder.buffer[encoder.offset + 2] = (x >> 3) & 0xff;
    encoder.buffer[encoder.offset + 3] = ((x << 5) & 0b1110_0000) | ((y >> 22) & 0b0001_1111);
    encoder.buffer[encoder.offset + 4] = (y >> 14) & 0xff;
    encoder.buffer[encoder.offset + 5] = (y >> 6) & 0xff;
    encoder.buffer[encoder.offset + 6] = ((y << 2) & 0b1111_1100) | ((p >> 8) & 0b0000_0011);
    encoder.buffer[encoder.offset + 7] = p & 0xff;
    encoder.offset += 8;
  }
}

export function readBufferedEncoderBuffer(encoder: BufferedEncoder) {
  const result = encoder.buffer.subarray(0, encoder.offset);
  encoder.offset = 0;
  return result;
}

export function clearBufferedEncoder(encoder: BufferedEncoder) {
  encoder.offset = 0;
}

function doubleCapacity(encoder: BufferedEncoder) {
  const newSize = encoder.buffer.byteLength * 2;

  try {
    const newBuffer = new Uint8Array(newSize);
    newBuffer.set(encoder.buffer);
    encoder.buffer = newBuffer;
  } catch (e: any) {
    throw new Error(`Failed to allocate buffer (size: ${newSize}, error: ${e.stack || e.message || e})`);
  }
}

export function createDecoder(handler: (x: number, y: number, p: number) => void): ToolDecoder {
  const x = createPredictor();
  const y = createPredictor();
  const p = createPredictor();
  return { x, y, p, handler, floats: false };
}

export function startDecoder(decoder: ToolDecoder, x: number, y: number, p: number) {
  decoder.floats = (x | 0) !== x || (y | 0) !== y;

  if (decoder.floats) {
    x = encodeCoord(x);
    y = encodeCoord(y);
  }

  predictorInit(decoder.x, x);
  predictorInit(decoder.y, y);
  predictorInit(decoder.p, p);
}

export function nextToolDecoder(decoder: ToolDecoder, x: number, y: number, p: number) {
  decoder.handler(x, y, p);

  if (!decoder.floats && ((x | 0) !== x || (y | 0) !== y)) {
    promoteToFloats(decoder);
    decoder.floats = true;
  }

  if (decoder.floats) {
    x = encodeCoord(x);
    y = encodeCoord(y);
  }

  predictorPush(decoder.x, x);
  predictorPush(decoder.y, y);
  predictorPush(decoder.p, p);
}

function nextMove(decoder: ToolDecoder, x: number, y: number, p: number) {
  let xx = x;
  let yy = y;

  if (decoder.floats) {
    xx = decodeCoord(xx);
    yy = decodeCoord(yy);
  }

  decoder.handler(xx, yy, p);

  predictorPush(decoder.x, x);
  predictorPush(decoder.y, y);
  predictorPush(decoder.p, p);
}

export function nextToolArrayDecoder(decoder: ToolDecoder, moves: Uint8Array) {
  for (let i = 0; i < moves.byteLength;) {
    const header = moves[i];
    const type = (header & 0b111) as BufferedEntry;
    let count = (header >> 3) + 1;
    i++;
    switch (type) {
      case BufferedEntry.Zero: {
        while (count--) {
          const x = predictorNext(decoder.x);
          const y = predictorNext(decoder.y);
          const p = predictorNext(decoder.p);
          nextMove(decoder, x, y, p);
        }
        break;
      }
      case BufferedEntry.U8: {
        while (count--) {
          // 2, 2, 4
          const a = moves[i];
          const dx = (a >> 6) & 0x03;
          const dy = (a >> 4) & 0x03;
          const dp = a & 0x0f;
          const x = predictorNext(decoder.x) + (dx > 1 ? dx - 4 : dx);
          const y = predictorNext(decoder.y) + (dy > 1 ? dy - 4 : dy);
          const p = predictorNext(decoder.p) + (dp > 7 ? dp - 16 : dp);
          nextMove(decoder, x, y, p);
          i += 1;
        }
        break;
      }
      case BufferedEntry.U16: {
        while (count--) {
          // 4, 4, 8
          const a = moves[i];
          const b = moves[i + 1];
          const dx = (b >> 4) & 0x0f;
          const dy = b & 0x0f;
          const dp = a;
          const x = predictorNext(decoder.x) + (dx > 7 ? dx - 16 : dx);
          const y = predictorNext(decoder.y) + (dy > 7 ? dy - 16 : dy);
          const p = predictorNext(decoder.p) + (dp > 127 ? dp - 256 : dp);
          nextMove(decoder, x, y, p);
          i += 2;
        }
        break;
      }
      case BufferedEntry.U24: {
        while (count--) {
          // 7, 7, 10
          const a = (moves[i] << 8) | (moves[i + 1] << 16) | moves[i + 2];
          const dx = (a >> 17) & 0x7f;
          const dy = (a >> 10) & 0x7f;
          const p = a & 0x3ff;
          const x = predictorNext(decoder.x) + (dx > 63 ? dx - 128 : dx);
          const y = predictorNext(decoder.y) + (dy > 63 ? dy - 128 : dy);
          nextMove(decoder, x, y, p);
          i += 3;
        }
        break;
      }
      case BufferedEntry.U32: {
        while (count--) {
          // 11, 11, 10
          const a = (moves[i] | (moves[i + 1] << 8) | (moves[i + 2] << 16) | (moves[i + 3] << 24)) >>> 0;
          const dx = (a >> 21) & 0x7ff;
          const dy = (a >> 10) & 0x7ff;
          const p = a & 0x3ff;
          const x = predictorNext(decoder.x) + (dx > 511 ? dx - 1024 : dx);
          const y = predictorNext(decoder.y) + (dy > 511 ? dy - 1024 : dy);
          nextMove(decoder, x, y, p);
          i += 4;
        }
        break;
      }
      case BufferedEntry.U40: {
        while (count--) {
          // 15, 15, 10
          const a = (moves[i] | (moves[i + 1] << 8) | (moves[i + 2] << 16) | (moves[i + 3] << 24)) >>> 0;
          const b = moves[i + 4];
          const tx = (a >> 17) & 0x7fff;
          const ty = (a >> 2) & 0x7fff;
          const x = tx > 16383 ? tx - 32767 : tx;
          const y = ty > 16383 ? ty - 32767 : ty;
          const p = ((a << 8) | b) & 0x3ff;
          nextMove(decoder, x, y, p);
          i += 5;
        }
        break;
      }
      case BufferedEntry.U64: {
        while (count--) {
          // 27, 27, 10
          const tx = ((moves[i + 0] << 19) | (moves[i + 1] << 11) | (moves[i + 2] << 3) | ((moves[i + 3] >> 5) & 0b111)) >>> 0;
          const ty = (((moves[i + 3] & 0b1_1111) << 22) | (moves[i + 4] << 14) | (moves[i + 5] << 6) | (moves[i + 6] >> 2)) >>> 0;
          const p = ((moves[i + 6] & 0b11) << 8) | moves[i + 7];
          const x = tx > 67108864 ? tx - 134217728 : tx;
          const y = ty > 67108864 ? ty - 134217728 : ty;
          nextMove(decoder, x, y, p);
          i += 8;
        }
        break;
      }
      case BufferedEntry.Floats:
        promoteToFloats(decoder);
        decoder.floats = true;
        break;
      default:
        invalidEnum(type);
    }
  }
}
